import {
  isWorkspaceScopedPage,
  pageLookup,
  legacyPageIdAliases
} from '../config/pages';
import type { ProjectRoute, PageId } from '../types/navigation';

export function normalizeRoutePageId(value?: string | null): PageId {
  if (value && value in legacyPageIdAliases) {
    return legacyPageIdAliases[value];
  }

  if (value && value in pageLookup) {
    return value as PageId;
  }

  return 'overview';
}

export function readRouteFromPath(pathname: string): ProjectRoute {
  if (!pathname) {
    return {
      kind: 'workspace',
      orgSlug: '',
      projectSlug: '',
      pageId: 'overview'
    };
  }

  const path = pathname.replace(/\/+$/, '') || '/';

  const workspaceRootMatch = path.match(/^\/org\/(?<orgSlug>[a-z0-9-]+)$/i);
  if (workspaceRootMatch?.groups?.orgSlug) {
    return {
      kind: 'workspace',
      orgSlug: workspaceRootMatch.groups.orgSlug,
      projectSlug: '',
      pageId: 'applications'
    };
  }

  if (path === '/signup') {
    return { kind: 'auth', authMode: 'signup' };
  }

  if (path === '/forgot-password') {
    return { kind: 'forgot-password' };
  }

  if (path === '/reset-password') {
    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    return { kind: 'reset-password', token: params.get('token') || '' };
  }

  if (path === '/onboarding') {
    return { kind: 'onboarding' };
  }

  if (path === '/accept-invite') {
    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    return { kind: 'accept-invite', token: params.get('token') || '' };
  }

  if (path === '/super-admin') {
    return { kind: 'super-admin' };
  }

  if (path === '/repo-link/callback') {
    const params = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    return {
      kind: 'repo-link-callback',
      verificationToken: params.get('verificationToken') || '',
      error: params.get('error') || ''
    };
  }

  const guideMatch = path.match(
    /^\/org\/(?<orgSlug>[a-z0-9-]+)\/guide(?:\/[a-z0-9-]+)?$/i
  );
  if (guideMatch?.groups?.orgSlug) {
    return {
      kind: 'workspace',
      orgSlug: guideMatch.groups.orgSlug,
      projectSlug: '',
      pageId: 'guide'
    };
  }

  const workspacePageMatch = path.match(
    /^\/org\/(?<orgSlug>[a-z0-9-]+)\/(?<page>inbox|work-radar|release-control|delivery-model|connections|agents|agents\/runners)$/i
  );
  if (workspacePageMatch?.groups?.orgSlug) {
    return {
      kind: 'workspace',
      orgSlug: workspacePageMatch.groups.orgSlug,
      projectSlug: '',
      pageId:
        workspacePageMatch.groups.page === 'release-control'
          ? 'shipments'
          : workspacePageMatch.groups.page === 'delivery-model'
            ? 'delivery-model'
            : workspacePageMatch.groups.page === 'connections'
              ? 'connections'
              : workspacePageMatch.groups.page === 'agents/runners'
                ? 'agent-runners'
                : workspacePageMatch.groups.page === 'agents'
                  ? 'agent-work'
                  : workspacePageMatch.groups.page === 'inbox'
                    ? 'work-inbox'
                    : 'pipeline-runs'
    };
  }

  const workspaceAdminMatch = path.match(
    /^\/org\/(?<orgSlug>[a-z0-9-]+)\/workspace\/(?<page>[a-z0-9-]+)$/i
  );
  if (
    workspaceAdminMatch?.groups?.orgSlug &&
    workspaceAdminMatch.groups.page &&
    isWorkspaceScopedPage(workspaceAdminMatch.groups.page)
  ) {
    return {
      kind: 'workspace',
      orgSlug: workspaceAdminMatch.groups.orgSlug,
      projectSlug: '',
      pageId: workspaceAdminMatch.groups.page
    };
  }

  if (path === '/login' || path === '/') {
    return { kind: 'auth', authMode: 'login' };
  }

  const match = path.match(
    /^\/org\/(?<orgSlug>[a-z0-9-]+)\/(?<segment>projects|apps)\/(?<projectSlug>[a-z0-9-]+)(?:\/(?<pageId>[a-z0-9-]+))?$/i
  );

  return {
    kind: 'workspace',
    orgSlug: match?.groups?.orgSlug || '',
    projectSlug: match?.groups?.projectSlug || '',
    pageId: normalizeRoutePageId(match?.groups?.pageId)
  };
}

export function buildWorkspacePath(
  organizationSlug: string,
  projectSlug: string,
  pageId: PageId
) {
  return `/org/${organizationSlug}/projects/${projectSlug}/${pageId}`;
}

export function buildWorkRadarPath(organizationSlug: string) {
  return `/org/${organizationSlug}/work-radar`;
}

export function buildWorkJourneyPath(
  organizationSlug: string,
  workJourneyId: string
) {
  const query = new URLSearchParams({ journey: workJourneyId });
  return `${buildWorkRadarPath(organizationSlug)}?${query.toString()}`;
}

export function buildWorkInboxPath(organizationSlug: string) {
  return `/org/${organizationSlug}/inbox`;
}

export function buildDeliveryModelPath(organizationSlug: string) {
  return `/org/${organizationSlug}/delivery-model`;
}

export function buildWorkspaceModulePath(
  organizationSlug: string,
  pageId: PageId
) {
  if (pageId === 'work-inbox') return buildWorkInboxPath(organizationSlug);
  if (pageId === 'pipeline-runs') return buildWorkRadarPath(organizationSlug);
  if (pageId === 'shipments') return buildReleaseControlPath(organizationSlug);
  if (pageId === 'delivery-model')
    return buildDeliveryModelPath(organizationSlug);
  if (pageId === 'connections') return `/org/${organizationSlug}/connections`;
  if (pageId === 'agent-runners')
    return `/org/${organizationSlug}/agents/runners`;
  if (pageId === 'agent-work') return `/org/${organizationSlug}/agents`;
  if (pageId === 'guide') return buildGuidePath(organizationSlug);
  return buildWorkspaceAdminPath(organizationSlug, pageId);
}

export function buildReleaseControlPath(organizationSlug: string) {
  return `/org/${organizationSlug}/release-control`;
}

export function buildGuidePath(organizationSlug: string, articleId?: string) {
  return `/org/${organizationSlug}/guide${articleId ? `/${articleId}` : ''}`;
}

export function buildWorkspaceAdminPath(
  organizationSlug: string,
  pageId: string
) {
  return `/org/${organizationSlug}/workspace/${pageId}`;
}
